<template>
    <div class="relative flex flex-col flex-1 min-h-0 bg-stage-background overflow-hidden">
        <loading :status="loading.form"/>

        <div class="stage-page-header">
            <div class="stage-page-header-row">
                <div class="stage-page-header-main">
                    <div class="flex items-center gap-3">
                        <h1 class="stage-page-title">Ticket</h1>
                        <identifier-badge :value="ticket.id || '-'" title="Ticket-ID"></identifier-badge>
                    </div>
                </div>
                <div class="stage-page-header-actions" v-on-clickaway="closeActionDropdown">
                    <button class="btn-stage-icon-plain" type="button" :title="$t('Back')" :aria-label="$t('Back')" @click="$router.push(ticketListPath)">
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.chevron-left"></svg-vue>
                    </button>
                    <div v-if="aiEnabled" class="relative inline-flex items-center">
                        <button
                            class="btn-stage-icon-plain"
                            type="button"
                            title="KI-Assistent"
                            aria-label="KI-Assistent"
                            @click="toggleAiSidebar"
                        >
                            <svg-vue class="h-4 w-4" icon="font-awesome.regular.hexagon-nodes"></svg-vue>
                        </button>
                    </div>
                    <div v-if="canManageCustomerRelease" class="relative inline-flex items-center text-left">
                        <button class="btn-stage-icon-plain" type="button" title="Kundenfreigabe" aria-label="Kundenfreigabe" @click="openSharedUsersDialog">
                            <svg-vue class="h-4 w-5" icon="font-awesome.regular.user-lock"></svg-vue>
                        </button>
                    </div>
                    <div v-if="canRename" class="relative inline-flex items-center text-left">
                        <button class="btn-stage-icon-plain" type="button" title="Optionen" aria-label="Optionen" @click="toggleActionDropdown('options')">
                            <svg-vue class="h-4 w-4" icon="font-awesome.regular.ellipsis-vertical"></svg-vue>
                        </button>
                        <div v-show="actions.options" class="origin-top-right absolute top-full right-0 mt-2 w-56 rounded shadow-lg z-20 bg-white border border-bordergray-100">
                            <div class="py-1 flex flex-col">
                                <button
                                    class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                    type="button"
                                    @click="openRenamePage"
                                >
                                    Ticket umbenennen
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="stage-page-gutters stage-page-fill relative pt-3 h-0">
            <div class="w-full h-full bg-white shadow sm:rounded border border-ultralight overflow-hidden">
                <div class="flex items-stretch h-full">
                    <div class="flex flex-col flex-auto min-w-0 min-h-0 h-full">
                        <div class="ticket-conversation ticket-conversation--fill grid h-full min-h-0" style="grid-template-rows: auto minmax(0, 1fr);">
                            <div class="block min-h-0">
                                <div class="stage-ticket-detail-header-surface flex items-stretch min-h-14 border-b border-ultralight">
                                    <div v-if="headerTicketProjects.length > 0" class="flex-shrink-0 border-r border-ultralight px-3 py-3 flex items-center">
                                        <div class="flex items-center flex-shrink-0">
                                            <template v-for="(project, projectIndex) in headerTicketProjects" :key="'ticket-header-project-' + Number(project.id) + '-' + projectIndex">
                                                <img
                                                    :src="ticketProjectImageUrl(project)"
                                                    :alt="project.title || 'Projektbild'"
                                                    :title="projectTooltip(project)"
                                                    :class="projectIndex === headerTicketProjects.length - 1 ? '' : '-mr-6'"
                                                    class="h-8 w-8 rounded-sm object-cover border border-white"
                                                >
                                            </template>
                                        </div>
                                    </div>
                                    <div class="flex-1 min-w-0 px-4 py-3 flex items-center">
                                        <div class="text-lg font-semibold text-gray-900 text-left truncate whitespace-nowrap overflow-hidden text-ellipsis">
                                            {{ ticketHeaderTitle }}
                                        </div>
                                    </div>
                                    <div class="w-48 flex-shrink-0 border-l border-r border-ultralight px-5 py-3 flex items-center">
                                        <span class="text-sm leading-5 font-base text-gray-700 whitespace-nowrap">{{ ticket.status ? ticket.status.name : '-' }}</span>
                                    </div>
                                    <div v-if="canViewPriorityHeader" class="w-40 flex-shrink-0 border-r border-ultralight px-5 py-3 flex items-center">
                                        <span class="text-sm leading-5 font-base text-gray-700 whitespace-nowrap">{{ ticket.priority ? ticket.priority.name : '-' }}</span>
                                    </div>
                                    <div class="w-28 flex-shrink-0">
                                        <button class="btn-stage-primary w-full h-full rounded-none" style="border-radius: 0;" type="button" @click="replyForm = true">
                                            Antworten
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div class="flex-1 min-h-0 overflow-y-auto">
                                <div v-show="replyForm" class="px-6 pt-6 pb-6 border-b border-ultralight">
                                    <loading :status="loading.reply"/>
                                    <form @submit.prevent="addReply">
                                        <input-wysiwyg
                                            id="ticket_body"
                                            v-model="ticketReply.body"
                                            :min-height="320"
                                            :plugins="{images: true, attachment: true, shortCode: true}"
                                            @selectUploadFile="selectUploadFile"
                                        >
                                            <template v-slot:top>
                                                <div :class="{'bg-gray-200': uploadingFileProgress > 0}" class="h-1 w-full">
                                                    <div :style="{width: uploadingFileProgress + '%'}" class="bg-blue-500 py-0.5"></div>
                                                </div>
                                            </template>
                                            <template v-slot:bottom>
                                                <div class="mt-3 flex items-center">
                                                    <button
                                                        :aria-label="$t('Discard')"
                                                        :title="$t('Discard')"
                                                        class="inline-flex items-center justify-center text-gray-400 focus:outline-none cursor-pointer mr-3"
                                                        type="button"
                                                        @click="discardReply"
                                                    >
                                                        <svg-vue class="h-4 w-4" icon="font-awesome.trash-alt-regular"></svg-vue>
                                                    </button>
                                                    <button class="btn btn-gray rounded-none ml-auto" type="submit">{{ $t('Send reply') }}</button>
                                                </div>
                                            </template>
                                        </input-wysiwyg>
                                    </form>
                                    <input ref="fileInput" hidden type="file" @change="uploadFile($event)">
                                    <template v-if="ticketReply.attachments.length > 0">
                                        <div class="mt-3 flex flex-wrap items-start gap-2">
                                            <template v-for="(attachment, index) in ticketReply.attachments" :key="'new-attachment-' + index">
                                                <attachment :details="attachment" v-on:remove="removeAttachment(index)"/>
                                            </template>
                                        </div>
                                    </template>
                                </div>

                                <template v-if="ticket.ticketReplies.length > 0">
                                    <div class="block">
                                        <template v-for="(ticketReply, index) in ticket.ticketReplies" :key="'ticket-reply-card-' + ticketReply.id + '-' + index">
                                            <div
                                                :class="index > 0 ? 'border-t border-ultralight' : ''"
                                                class="flex p-6"
                                            >
                                                <img
                                                    :alt="$t('Avatar')"
                                                    :src="ticketReply.user.avatar !== 'gravatar' ? ticketReply.user.avatar : ticketReply.user.gravatar"
                                                    class="h-9 w-9 hidden sm:inline rounded-full object-cover"
                                                />
                                                <div class="sm:pl-6 pb-2 w-full">
                                                    <div class="md:flex md:items-center pb-1">
                                                        <div class="flex-1 min-w-0 text-lg font-semibold text-gray-800">
                                                            {{ ticketReply.user.name }}
                                                        </div>
                                                        <div class="flex-shrink-0 md:ml-4">
                                                            <div class="text-sm text-gray-500 whitespace-nowrap md:text-right">
                                                                {{ $momentFormatDateTime(ticketReply.created_at) }}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <p class="text-gray-600 font-base ticket-reply-body" v-html="ticketReply.body"/>
                                                    <template v-if="ticketReply.attachments.length > 0">
                                                        <div class="mt-5 flex flex-wrap items-start gap-2">
                                                            <template v-for="attachment in ticketReply.attachments" :key="'reply-attachment-' + attachment.id">
                                                                <attachment :details="attachment" :remove-button="false"/>
                                                            </template>
                                                        </div>
                                                    </template>
                                                </div>
                                            </div>
                                        </template>
                                    </div>
                                </template>
                                <template v-else>
                                    <empty-state
                                        class="border-t border-ultralight"
                                        :title="$t('This ticket has no conversations')"
                                    />
                                </template>
                            </div>
                        </div>
                    </div>

                    <div
                        v-if="aiEnabled && aiSidebarOpen"
                        :style="{flex: '0 0 352px', width: '352px'}"
                        class="hidden lg:block border-l border-ultralight"
                    >
                        <div class="h-full py-4 px-6 overflow-y-auto bg-white">
                            <div class="flex items-center justify-between mb-3">
                                <div class="text-xl font-semibold text-gray-900">KI-Assistent</div>
                                <button class="text-gray-400 hover:text-gray-600" type="button" @click="aiSidebarOpen = false">
                                    <svg-vue class="w-4 h-4" icon="font-awesome.times-light"></svg-vue>
                                </button>
                            </div>

                            <div class="relative min-h-[96px] mb-6">
                                <loading :status="aiLoading"/>
                                <div
                                    v-if="!aiLoading"
                                    class="text-sm leading-5 text-gray-600 whitespace-pre-line break-words"
                                    style="hyphens: auto;"
                                >
                                    {{ aiResult.reply || 'Noch keine Zusammenfassung vorhanden.' }}
                                </div>
                            </div>

                            <div v-if="!aiLoading && aiResult.actions.length > 0" class="mt-4 bg-white border border-l-4 border-l-orange-200 rounded overflow-hidden" style="border-color: var(--ui-border-subtle); border-left-color: #f97316;">
                                <div class="px-3 py-2 text-xs font-semibold text-gray-700 tracking-wide" style="background-color: var(--ui-surface-raised);">{{ $t('AI category actions') }}</div>
                                <div class="px-3">
                                    <div
                                        v-for="(item, index) in aiResult.actions"
                                        :key="'detail-ai-action-' + index"
                                        class="py-2 text-xs leading-5 text-gray-700"
                                        :class="index === 0 ? '' : 'border-t'"
                                        style="border-color: var(--ui-border-subtle);"
                                    >
                                        {{ item }}
                                    </div>
                                </div>
                            </div>

                            <div v-if="!aiLoading && aiResult.alerts.length > 0" class="mt-4 bg-white border border-l-4 border-l-red-200 rounded overflow-hidden" style="border-color: var(--ui-border-subtle); border-left-color: #ef4444;">
                                <div class="px-3 py-2 text-xs font-semibold text-gray-700 tracking-wide" style="background-color: var(--ui-surface-raised);">{{ $t('AI category alerts') }}</div>
                                <div class="px-3">
                                    <div
                                        v-for="(item, index) in aiResult.alerts"
                                        :key="'detail-ai-alert-' + index"
                                        class="py-2 text-xs leading-5 text-gray-700"
                                        :class="index === 0 ? '' : 'border-t'"
                                        style="border-color: var(--ui-border-subtle);"
                                    >
                                        {{ item }}
                                    </div>
                                </div>
                            </div>

                            <div v-if="!aiLoading && aiResult.warnings.length > 0" class="mt-4 bg-white border border-l-4 border-l-yellow-200 rounded overflow-hidden" style="border-color: var(--ui-border-subtle); border-left-color: #eab308;">
                                <div class="px-3 py-2 text-xs font-semibold text-gray-700 tracking-wide" style="background-color: var(--ui-surface-raised);">{{ $t('AI category warnings') }}</div>
                                <div class="px-3">
                                    <div
                                        v-for="(item, index) in aiResult.warnings"
                                        :key="'detail-ai-warning-' + index"
                                        class="py-2 text-xs leading-5 text-gray-700"
                                        :class="index === 0 ? '' : 'border-t'"
                                        style="border-color: var(--ui-border-subtle);"
                                    >
                                        {{ item }}
                                    </div>
                                </div>
                            </div>

                            <div v-if="!aiLoading && aiResult.notes.length > 0" class="mt-4 bg-white border border-l-4 border-l-blue-200 rounded overflow-hidden" style="border-color: var(--ui-border-subtle); border-left-color: #3b82f6;">
                                <div class="px-3 py-2 text-xs font-semibold text-gray-700 tracking-wide" style="background-color: var(--ui-surface-raised);">{{ $t('AI category notes') }}</div>
                                <div class="px-3">
                                    <div
                                        v-for="(item, index) in aiResult.notes"
                                        :key="'detail-ai-note-' + index"
                                        class="py-2 text-xs leading-5 text-gray-700"
                                        :class="index === 0 ? '' : 'border-t'"
                                        style="border-color: var(--ui-border-subtle);"
                                    >
                                        {{ item }}
                                    </div>
                                </div>
                            </div>

                            <div v-if="!aiLoading && aiResult.links.length > 0" class="mt-4 bg-white border rounded overflow-hidden" style="border-color: var(--ui-border-subtle);">
                                <div class="px-3">
                                    <div
                                        v-for="(link, index) in aiResult.links"
                                        :key="'detail-ai-link-' + index"
                                        class="flex items-start gap-2 py-2"
                                        :class="index === 0 ? '' : 'border-t'"
                                        style="border-color: var(--ui-border-subtle);"
                                    >
                                        <span class="text-gray-500 leading-5 flex-shrink-0" aria-hidden="true">
                                            <svg class="w-3 h-3 mt-1" fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 6H17a4 4 0 0 1 0 8h-3.5m-3-4H7a4 4 0 0 0 0 8h3.5m-2-8h7" />
                                            </svg>
                                        </span>
                                        <a
                                            :href="link.url"
                                            class="text-xs leading-5 text-gray-700 hover:text-gray-900 break-all"
                                            rel="noopener noreferrer"
                                            target="_blank"
                                        >
                                            {{ link.label }}
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div
                        :style="{flex: '0 0 352px', width: '352px'}"
                        class="hidden lg:block border-l border-ultralight"
                    >
                        <div class="h-full overflow-y-auto">
                            <div v-if="creatorSidebarUser" class="mx-6 pt-6 pb-6 mb-6 border-b border-ultralight">
                                <div class="flex items-start">
                                    <img
                                        :alt="$t('Avatar')"
                                        :src="creatorSidebarUser.avatar !== 'gravatar' ? creatorSidebarUser.avatar : creatorSidebarUser.gravatar"
                                        class="h-14 w-14 rounded-full object-cover"
                                    />
                                </div>
                                <div class="mt-2 min-w-0">
                                    <div class="mb-2 font-semibold text-base text-gray-800 truncate">{{ creatorSidebarUser.name || '-' }}</div>
                                    <div class="mb-1 text-sm leading-4 text-gray-500 truncate">
                                        {{ creatorSidebarUser.email || '-' }}
                                    </div>
                                    <div v-if="getUserPhone(creatorSidebarUser)" class="text-sm leading-4 text-gray-500 truncate">
                                        {{ getUserPhone(creatorSidebarUser) }}
                                    </div>
                                </div>
                            </div>
                            <div v-if="recipientSidebarUsers.length > 0" class="px-6 pb-6">
                                <div class="text-xs tracking-wide text-gray-500 mb-2">
                                    {{ $t('Recipient') }}
                                </div>
                                <div class="space-y-4">
                                    <template v-for="person in recipientSidebarUsers" :key="'sidebar-recipient-user-' + person.id">
                                        <div class="flex items-start">
                                            <div class="mr-3 flex-shrink-0">
                                                <img
                                                    :alt="$t('Avatar')"
                                                    :src="person.avatar !== 'gravatar' ? person.avatar : person.gravatar"
                                                    class="h-9 w-9 rounded-full object-cover"
                                                />
                                            </div>
                                            <div class="min-w-0 flex-1">
                                                <div class="text-gray-800 font-regular text-sm truncate">{{ person.name }}</div>
                                                <div class="text-sm leading-4 text-gray-500 truncate">
                                                    {{ person.email || '-' }}
                                                </div>
                                            </div>
                                        </div>
                                    </template>
                                </div>
                            </div>
                            <div v-if="sharedSidebarUsers.length > 0" class="px-6 pb-6">
                                <div class="text-xs tracking-wide text-gray-500 mb-2">
                                    {{ $t('Geteilt') }}
                                </div>
                                <div class="space-y-4">
                                    <template v-for="person in sharedSidebarUsers" :key="'sidebar-shared-user-' + person.id">
                                        <div class="flex items-start">
                                            <div class="mr-3 flex-shrink-0">
                                                <img
                                                    :alt="$t('Avatar')"
                                                    :src="person.avatar !== 'gravatar' ? person.avatar : person.gravatar"
                                                    class="h-9 w-9 rounded-full object-cover"
                                                />
                                            </div>
                                            <div class="min-w-0 flex-1">
                                                <div class="text-gray-800 font-regular text-sm truncate">{{ person.name }}</div>
                                                <div class="text-sm leading-4 text-gray-500 truncate">
                                                    {{ person.email || '-' }}
                                                </div>
                                            </div>
                                        </div>
                                    </template>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="dialogs.sharedUsers" class="fixed inset-0 z-40 flex items-center justify-center bg-black/40 px-4">
            <div class="w-full max-w-2xl h-[28.5rem] rounded-lg bg-white shadow-xl overflow-hidden flex flex-col">
                <div class="flex items-center justify-between px-6 py-3 stage-header-surface border-b border-ultralight">
                    <div class="text-xl font-semibold text-gray-900">{{ $t('Shared') }}</div>
                    <button class="text-gray-400 hover:text-gray-600" type="button" @click="closeSharedUsersDialog">
                        <svg-vue class="h-4 w-4" icon="font-awesome.times-light"></svg-vue>
                    </button>
                </div>
                <div class="flex-1 min-h-0 overflow-hidden">
                    <div class="h-full overflow-y-auto">
                        <div class="divide-y divide-ultralight">
                            <label
                                v-for="customer in availableSharedUsers"
                                :key="'ticket-customer-release-dialog-' + customer.id"
                                class="flex items-center px-6 py-3 text-sm text-gray-700 cursor-pointer"
                            >
                                <input
                                    v-model="draftCustomerIds"
                                    :value="customer.id"
                                    class="form-checkbox h-4 w-4 text-gray-600 transition duration-150 ease-in-out mr-4"
                                    type="checkbox"
                                >
                                <img
                                    :alt="$t('Avatar')"
                                    :src="customer.avatar !== 'gravatar' ? customer.avatar : customer.gravatar"
                                    class="h-6 w-6 rounded-full object-cover mr-3"
                                />
                                <span class="block truncate">{{ customer.name }}</span>
                            </label>
                        </div>
                    </div>
                </div>
                <div class="flex items-center justify-end gap-3 px-6 py-4 stage-footer-surface border-t border-ultralight">
                    <button class="btn btn-white" type="button" @click="closeSharedUsersDialog">{{ $t('Cancel') }}</button>
                    <button class="btn btn-gray" type="button" @click="saveCustomers">{{ $t('Save') }}</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {mixin as clickaway} from "vue-clickaway";
import {refreshPwaBadgeCount} from "@/utils/pwa-badge";
import {resolveStoreAccess} from "@/utils/access-context";
import {resolveReturnToPath} from "@/utils/list-route-state";

export default {
    name: "ticket-manage-customer",
    mixins: [clickaway],
    metaInfo() {
        return {
            title: this.$i18n.t('Ticket details')
        }
    },
    data() {
        return {
            loading: {
                form: true,
                reply: false,
                file: false,
            },
            actions: {
                options: false,
            },
            dialogs: {
                sharedUsers: false,
            },
            replyForm: false,
            uploadingFileProgress: 0,
            ticket: {
                id: null,
                subject: null,
                status: null,
                priority: null,
                labels: [],
                sender: null,
                sender_user_id: null,
                recipient: null,
                recipient_user_id: null,
                agents: [],
                customers: [],
                created_at: null,
                ticketReplies: [],
            },
            ticketReply: {
                status_id: null,
                body: '',
                attachments: [],
            },
            selectedCustomerIds: [],
            draftCustomerIds: [],
            assignableUsers: [],
            aiEnabled: false,
            aiSidebarOpen: false,
            aiLoading: false,
            aiResult: {
                reply: '',
                actions: [],
                alerts: [],
                warnings: [],
                notes: [],
                links: [],
            },
        }
    },
    computed: {
        currentUser() {
            return this.$store?.state?.user || null;
        },
        accessContext() {
            return resolveStoreAccess(this.$store);
        },
        isGuestUser() {
            return !!this.accessContext.is_guest;
        },
        isCustomerUser() {
            return !!this.accessContext.is_customer;
        },
        canViewPriorityHeader() {
            return !this.isGuestUser && !this.isCustomerUser;
        },
        ticketListPath() {
            return resolveReturnToPath(this.$route.query || {}, '/tickets/list');
        },
        canManageCustomerRelease() {
            return this.canRename && this.availableSharedUsers.length > 0;
        },
        canRename() {
            const currentUserId = Number(this.$store?.state?.user?.id || 0);
            return currentUserId > 0 && currentUserId === Number(this.ticket.creator_user_id || this.ticket.sender_user_id || 0);
        },
        ticketHeaderTitle() {
            const subject = String(this.ticket && this.ticket.subject ? this.ticket.subject : '').trim();
            return subject !== '' ? subject : '-';
        },
        headerLabels() {
            return Array.isArray(this.ticket && this.ticket.labels)
                ? this.ticket.labels
                    .map((label, index) => ({label, index}))
                    .filter((item) => item.label && String(item.label.name || '').trim() !== '')
                : [];
        },
        headerTicketProjects() {
            return this.getTicketProjects(this.ticket);
        },
        creatorSidebarUser() {
            return this.ticket?.creator || this.ticket?.sender || this.ticket?.recipient || null;
        },
        recipientSidebarUsers() {
            const recipient = this.ticket?.recipient || null;
            const recipientId = Number(recipient?.id || 0);
            const creatorId = Number(this.creatorSidebarUser?.id || 0);

            if (!recipientId || recipientId === creatorId) {
                return [];
            }

            return [recipient];
        },
        approvalExcludedUserIds() {
            const ids = [
                Number(this.creatorSidebarUser?.id || 0),
                Number(this.ticket?.creator_user_id || 0),
                Number(this.ticket?.recipient?.id || 0),
                Number(this.ticket?.recipient_user_id || 0),
            ];

            return [...new Set(ids.filter((id) => id > 0))];
        },
        internalApprovalUsers() {
            const recipients = [];
            const seenStaff = new Set();
            const mainAdminId = Number(this.ticket?.main_admin?.id || 0);
            const excludedIds = new Set(this.approvalExcludedUserIds);

            const pushStaff = (person) => {
                const id = Number(person && person.id ? person.id : 0);
                if (!id || excludedIds.has(id) || seenStaff.has(id)) {
                    return;
                }
                seenStaff.add(id);
                recipients.push(person);
            };

            if (mainAdminId && this.ticket.main_admin) {
                pushStaff(this.ticket.main_admin);
            }

            (this.ticket.agents || []).forEach((person) => pushStaff(person));

            return recipients;
        },
        sharedUsers() {
            const seen = new Set(this.internalApprovalUsers.map((person) => Number(person?.id || 0)));
            const shared = [];
            const excludedIds = new Set(this.approvalExcludedUserIds);

            (this.ticket.customers || []).forEach((person) => {
                const id = Number(person && person.id ? person.id : 0);
                if (!id || excludedIds.has(id) || seen.has(id)) {
                    return;
                }
                seen.add(id);
                shared.push(person);
            });

            return shared;
        },
        customerApprovalUsers() {
            return this.sharedUsers;
        },
        guestApprovalUsers() {
            const seen = new Set();
            const guests = [];
            const excludedIds = new Set(this.approvalExcludedUserIds);

            (this.ticket.guests || []).forEach((person) => {
                const id = Number(person && person.id ? person.id : 0);
                if (!id || excludedIds.has(id) || seen.has(id)) {
                    return;
                }

                seen.add(id);
                guests.push(person);
            });

            return guests;
        },
        sharedSidebarUsers() {
            const shared = [];
            const seen = new Set();

            [...this.internalApprovalUsers, ...this.customerApprovalUsers, ...this.guestApprovalUsers].forEach((person) => {
                const id = Number(person && person.id ? person.id : 0);
                if (!id || seen.has(id)) {
                    return;
                }

                seen.add(id);
                shared.push(person);
            });

            return shared;
        },
        availableSharedUsers() {
            const excludedIds = new Set(this.approvalExcludedUserIds);
            return (this.assignableUsers || []).filter((person) => !excludedIds.has(Number(person?.id || 0)));
        },
    },
    filters: {
        momentFormatDateTime: function (value) {
            return moment(value).locale(window.app.app_date_locale).format(window.app.app_date_format + ' HH:mm');
        },
        momentFormatDateTimeAgo: function (value) {
            return moment(value).locale(window.app.app_date_locale).fromNow();
        },
    },
    mounted() {
        this.getTicket();
        this.getAssignableUsers();
    },
    methods: {
        normalizeAiEnabled(value) {
            return value === true || value === 1 || value === '1';
        },
        getTicket() {
            const self = this;
            self.loading.form = true;
            axios.get('api/tickets/' + self.$route.params.id).then(function (response) {
                self.loading.form = false;
                self.ticket = response.data;
                self.aiEnabled = self.normalizeAiEnabled(response.data.ai_enabled);
                if (!self.aiEnabled) {
                    self.aiSidebarOpen = false;
                }
                self.ticketReply.status_id = response.data.status_id;
                self.selectedCustomerIds = (response.data.customers || []).map(function (customer) {
                    return Number(customer.id);
                }).filter(function (id) {
                    return id > 0
                        && id !== Number(response.data.creator_user_id || response.data.sender_user_id || 0)
                        && id !== Number(response.data.recipient_user_id || 0);
                });
                self.draftCustomerIds = [...self.selectedCustomerIds];
                self.markTicketAsRead();
            }).catch(function () {
                self.$router.push('/tickets/list');
            });
        },
        markTicketAsRead() {
            if (!this.ticket?.id) {
                return;
            }

            axios.post('api/tickets/' + this.ticket.id + '/mark-read').then(function () {
                refreshPwaBadgeCount().catch(function () {
                });
            }).catch(function () {});
        },
        getAssignableUsers() {
            const self = this;
            axios.get('api/tickets/assignable-users', {
                params: {
                    ticket_id: self.$route.params.id,
                },
            }).then(function (response) {
                self.assignableUsers = self.normalizeCollection(response.data);
            }).catch(function () {
                self.assignableUsers = [];
            });
        },
        normalizeCollection(collection) {
            if (Array.isArray(collection)) {
                return collection;
            }
            if (collection && Array.isArray(collection.data)) {
                return collection.data;
            }
            return [];
        },
        toggleAiSidebar() {
            if (!this.aiEnabled) {
                return;
            }

            this.closeActionDropdown();
            this.aiSidebarOpen = !this.aiSidebarOpen;
            if (this.aiSidebarOpen) {
                this.runAiSummary();
            }
        },
        normalizeAiStringList(value) {
            const values = Array.isArray(value) ? value : [value];
            return values
                .map((entry) => String(entry || '').trim())
                .filter((entry) => entry !== '');
        },
        normalizeAiLinks(value) {
            const values = Array.isArray(value) ? value : [];
            return values.map((entry) => {
                if (entry && typeof entry === 'object') {
                    const label = String(entry.label || '').trim();
                    const url = this.normalizeAiLinkUrl(String(entry.url || '').trim());
                    return {
                        label: label || url,
                        url: url || '#',
                    };
                }
                const raw = this.normalizeAiLinkUrl(String(entry || '').trim());
                return {
                    label: raw,
                    url: raw || '#',
                };
            }).filter((entry) => entry.url && entry.url !== '#');
        },
        normalizeAiLinkUrl(rawUrl) {
            const url = String(rawUrl || '').trim();
            if (url === '') {
                return '';
            }

            const lower = url.toLowerCase();
            const isFileApiLink = lower.includes('/api/files/download/') || lower.includes('/api/files/preview/');
            if (!isFileApiLink) {
                return url;
            }

            if (lower.includes('token=')) {
                return url;
            }

            const token = typeof getAuthToken === 'function' ? getAuthToken() : '';
            if (!token) {
                return url;
            }

            return url + (url.includes('?') ? '&' : '?') + 'token=' + encodeURIComponent(token);
        },
        runAiSummary() {
            if (this.aiLoading || !this.ticket || !this.ticket.id) {
                return;
            }

            this.aiLoading = true;

            axios.post('api/tickets/ai-assistant', {
                ticket_id: Number(this.ticket.id),
            }).then((response) => {
                this.aiResult = {
                    reply: String(response.data?.reply || response.data?.summary || response.data?.answer || 'Es konnte keine Zusammenfassung erstellt werden.'),
                    actions: this.normalizeAiStringList(response.data?.actions || response.data?.action_items),
                    alerts: this.normalizeAiStringList(response.data?.alerts),
                    warnings: this.normalizeAiStringList(response.data?.warnings),
                    notes: this.normalizeAiStringList(response.data?.notes),
                    links: this.normalizeAiLinks(response.data?.links),
                };
            }).catch((error) => {
                const message = String(error?.response?.data?.message || 'Die Anfrage konnte nicht verarbeitet werden.');
                this.aiResult = {
                    reply: 'Fehler: ' + message,
                    actions: [],
                    alerts: [],
                    warnings: [],
                    notes: [],
                    links: [],
                };
            }).finally(() => {
                this.aiLoading = false;
            });
        },
        getTicketProjects(ticket) {
            const projects = Array.isArray(ticket?.projects)
                ? ticket.projects
                : (ticket?.projects && Array.isArray(ticket.projects.data) ? ticket.projects.data : []);
            if (projects.length > 0) {
                return projects
                    .filter((project) => project && Number(project.id) > 0);
            }

            if (ticket && ticket.project && Number(ticket.project.id) > 0) {
                return [ticket.project];
            }

            return [];
        },
        ticketProjectImageUrl(project) {
            if (project && project.image_url) {
                return project.image_url;
            }

            return '/images/default/icon.png';
        },
        projectTooltip(project) {
            const title = String(project?.title || '').trim();
            return title !== '' ? `Projekt: ${title}` : 'Projekt';
        },
        addReply() {
            const self = this;
            self.loading.reply = true;
            axios.post('api/tickets/' + self.$route.params.id + '/reply', self.ticketReply).then(function (response) {
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data saved correctly').toString(),
                    type: 'success'
                });
                self.discardReply();
                self.ticket = response.data.ticket;
                self.selectedCustomerIds = (response.data.ticket.customers || []).map(function (customer) {
                    return Number(customer.id);
                }).filter(function (id) {
                    return id > 0
                        && id !== Number(response.data.ticket.creator_user_id || response.data.ticket.sender_user_id || 0)
                        && id !== Number(response.data.ticket.recipient_user_id || 0);
                });
                self.loading.reply = false;
            }).catch(function () {
                self.loading.reply = false;
            });
        },
        discardReply() {
            this.ticketReply.body = '';
            this.ticketReply.attachments = [];
            this.replyForm = false;
        },
        selectUploadFile() {
            if (!this.loading.file) {
                this.$refs.fileInput.click();
            } else {
                this.$notify({
                    title: this.$i18n.t('Error').toString(),
                    text: this.$i18n.t('A file is being uploaded').toString(),
                    type: 'warning'
                });
            }
        },
        uploadFile(e) {
            const self = this;
            const formData = new FormData();
            self.loading.file = true;
            formData.append('file', e.target.files[0]);
            axios.post(
                'api/tickets/attachments',
                formData,
                {
                    headers: {'Content-Type': 'multipart/form-data'},
                    onUploadProgress: function (progressEvent) {
                        self.uploadingFileProgress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
                    }.bind(this)
                }
            ).then(function (response) {
                self.loading.file = false;
                self.uploadingFileProgress = 0;
                self.$refs.fileInput.value = null;
                self.ticketReply.attachments.push(response.data);
            }).catch(function () {
                self.loading.file = false;
                self.uploadingFileProgress = 0;
                self.$refs.fileInput.value = null;
            });
        },
        removeAttachment(index) {
            this.ticketReply.attachments.splice(index, 1);
        },
        saveCustomers() {
            const self = this;
            axios.post('api/tickets/' + self.$route.params.id + '/participants', {
                customer_ids: self.draftCustomerIds,
            }).then(function (response) {
                self.ticket = response.data.ticket;
                self.selectedCustomerIds = (response.data.ticket.customers || []).map(function (customer) {
                    return Number(customer.id);
                }).filter(function (id) {
                    return id > 0
                        && id !== Number(response.data.ticket.creator_user_id || response.data.ticket.sender_user_id || 0)
                        && id !== Number(response.data.ticket.recipient_user_id || 0);
                });
                self.draftCustomerIds = [...self.selectedCustomerIds];
                self.dialogs.sharedUsers = false;
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data saved correctly').toString(),
                    type: 'success'
                });
            }).catch(function () {
                self.selectedCustomerIds = (self.ticket.customers || []).map(function (customer) {
                    return Number(customer.id);
                }).filter(function (id) {
                    return id > 0
                        && id !== Number(self.ticket.creator_user_id || self.ticket.sender_user_id || 0)
                        && id !== Number(self.ticket.recipient_user_id || 0);
                });
                self.draftCustomerIds = [...self.selectedCustomerIds];
                self.$notify({
                    title: self.$i18n.t('Error').toString(),
                    text: self.$i18n.t('An error occurred while saving data').toString(),
                    type: 'error'
                });
            });
        },
        closeActionDropdown() {
            this.actions.options = false;
        },
        toggleActionDropdown(action) {
            if (action === 'options') {
                this.actions.options = !this.actions.options;
            }
        },
        openSharedUsersDialog() {
            this.closeActionDropdown();
            this.draftCustomerIds = [...this.selectedCustomerIds];
            this.dialogs.sharedUsers = true;
        },
        closeSharedUsersDialog() {
            this.draftCustomerIds = [...this.selectedCustomerIds];
            this.dialogs.sharedUsers = false;
        },
        openRenamePage() {
            this.closeActionDropdown();
            this.$router.push('/tickets/' + this.$route.params.id + '/edit');
        },
        getUserPhone(user) {
            return user?.phone || user?.phone_mobile || user?.phone_landline || null;
        },
        getUserCompanyName(user) {
            return user && user.company_name ? user.company_name : null;
        },
    }
}
</script>
